import { ActionType, createAction, createReducer } from 'typesafe-actions';
import { combineReducers } from 'redux';
import { AuthorDetailTimeUsage, ReviewFormFieldError } from './types';
import { Validators } from './validators';

export namespace Actions {
  export const resetFormFields = createAction('reviews/REVIEW_FORM/reset')();
  export const setTotalScore = createAction('reviews/REVIEW_FORM/setTotalScore')<number>();
  export const setReviewBodyText = createAction('reviews/REVIEW_FORM/setReviewBodyText')<string>();
  export const setReviewProsText = createAction('reviews/REVIEW_FORM/setReviewProsText')<string>();
  export const setReviewConsText = createAction('reviews/REVIEW_FORM/setReviewConsText')<string>();
  export const setAuthorName = createAction('reviews/REVIEW_FORM/setAuthorName')<string>();
  export const setAuthorEmail = createAction('reviews/REVIEW_FORM/setAuthorEmail')<string>();
  export const setAuthorDetailShop = createAction('reviews/REVIEW_FORM/setAuthorDetailShop')<
    string | null
  >();
  export const setAuthorDetailTimeUsage = createAction('reviews/REVIEW_FORM/setAuthorDetailTimeUsage')<{
    timeUsage: AuthorDetailTimeUsage['value'];
    indexFromOne: number;
  }>();
}

export namespace Reducers {
  export type TotalScoreActions = ActionType<typeof Actions.setTotalScore>;
  export const totalScore = createReducer<number | null, TotalScoreActions>(null).handleAction(
    Actions.setTotalScore,
    (_, { payload }) => payload,
  );

  export type BodyTextActions = ActionType<typeof Actions.setReviewBodyText>;
  export const bodyText = createReducer<string | null, BodyTextActions>(null).handleAction(
    Actions.setReviewBodyText,
    (_, { payload }) => payload,
  );

  export type ProsTextActions = ActionType<typeof Actions.setReviewProsText>;
  export const prosText = createReducer<string | null, ProsTextActions>(null).handleAction(
    Actions.setReviewProsText,
    (_, { payload }) => payload,
  );

  export type ConsTextActions = ActionType<typeof Actions.setReviewConsText>;
  export const consText = createReducer<string | null, ConsTextActions>(null).handleAction(
    Actions.setReviewConsText,
    (_, { payload }) => payload,
  );

  export type AuthorDetailTimeUsageActions = ActionType<typeof Actions.setAuthorDetailTimeUsage>;
  export const authorTimeUsage = createReducer<AuthorDetailTimeUsage | null, AuthorDetailTimeUsageActions>(
    null,
  ).handleAction(Actions.setAuthorDetailTimeUsage, (_, { payload }) => ({
    key: 'time_usage',
    value: payload.timeUsage,
  }));

  export type AuthorDetailShopActions = ActionType<typeof Actions.setAuthorDetailShop>;
  export const authorShop = createReducer<string | null, AuthorDetailShopActions>(null).handleAction(
    Actions.setAuthorDetailShop,
    (_, { payload }) => payload,
  );

  export type AuthorNameActions = ActionType<typeof Actions.setAuthorName>;
  export const authorName = createReducer<string | null, AuthorNameActions>(null).handleAction(
    Actions.setAuthorName,
    (_, { payload }) => payload,
  );

  export type AuthorEmailActions = ActionType<typeof Actions.setAuthorEmail>;
  export const authorEmail = createReducer<string | null, AuthorEmailActions>(null).handleAction(
    Actions.setAuthorEmail,
    (_, { payload }) => payload,
  );

  export type RequiredFieldsAction =
    | TotalScoreActions
    | BodyTextActions
    | AuthorNameActions
    | AuthorEmailActions;
  export const requiredFieldsAreEmpty = createReducer<boolean, RequiredFieldsAction>(false)
    .handleAction([Actions.setTotalScore], (state, { payload }) => state || payload === 0)
    .handleAction(
      [Actions.setReviewBodyText, Actions.setAuthorName, Actions.setAuthorEmail],
      (state, { payload }) => state || payload.length === 0,
    );

  const fieldValidatorAsReducer =
    <T>(fieldKey: ReviewFormFieldError['fieldKey'], validatorFn: Validators.ValidatorFn<T>) =>
    (state: ReviewFormFieldError[], { payload }: { payload: T }) => {
      const result = state.filter((r) => r.fieldKey !== fieldKey);
      result.push(...validatorFn(payload));
      return result;
    };

  export type FieldsErrorsActions =
    | TotalScoreActions
    | BodyTextActions
    | ProsTextActions
    | ConsTextActions
    | AuthorNameActions
    | AuthorEmailActions;
  export const fieldsErrors = createReducer<ReviewFormFieldError[], FieldsErrorsActions>([])
    .handleAction(
      Actions.setTotalScore,
      fieldValidatorAsReducer('rating', Validators.totalScoreValidator('rating')),
    )
    .handleAction(
      Actions.setReviewBodyText,
      fieldValidatorAsReducer('body', Validators.bodyTextValidator('body')),
    )
    .handleAction(
      Actions.setReviewProsText,
      fieldValidatorAsReducer('pros', Validators.prosConsValidator('pros')),
    )
    .handleAction(
      Actions.setReviewConsText,
      fieldValidatorAsReducer('cons', Validators.prosConsValidator('cons')),
    )
    .handleAction(
      Actions.setAuthorName,
      fieldValidatorAsReducer('author_name', Validators.authorNameValidator('author_name')),
    )
    .handleAction(
      Actions.setAuthorEmail,
      fieldValidatorAsReducer('author_email', Validators.authorEmailValidator('author_email')),
    );

  export type FormReducerActions =
    | TotalScoreActions
    | BodyTextActions
    | ProsTextActions
    | ConsTextActions
    | AuthorDetailTimeUsageActions
    | AuthorDetailShopActions
    | AuthorNameActions
    | AuthorEmailActions;

  export const formState = combineReducers({
    totalScore,
    bodyText,
    prosText,
    consText,
    authorTimeUsage,
    authorShop,
    authorName,
    authorEmail,
    fieldsErrors,
    requiredFieldsAreEmpty,
  });

  export type FormState = ReturnType<typeof formState>;
}
